/**
 * Circuit Breaker Pattern - Prévient les appels excessifs en cas d'erreurs répétées
 */
export class CircuitBreaker {
  private static instances = new Map<string, CircuitBreaker>();
  
  private failures = 0;
  private lastFailureTime = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  
  constructor(
    private readonly failureThreshold = 5,
    private readonly recoveryTimeout = 30000, // 30 secondes
    private readonly identifier: string = 'default'
  ) {}

  static getInstance(identifier: string, failureThreshold = 5, recoveryTimeout = 30000): CircuitBreaker {
    if (!this.instances.has(identifier)) {
      this.instances.set(identifier, new CircuitBreaker(failureThreshold, recoveryTimeout, identifier));
    }
    return this.instances.get(identifier)!;
  }

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime < this.recoveryTimeout) {
        throw new Error(`Circuit breaker is OPEN for ${this.identifier}. Too many recent failures.`);
      } else {
        this.state = 'HALF_OPEN';
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.failures = 0;
    this.state = 'CLOSED';
  }

  private onFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();
    
    if (this.failures >= this.failureThreshold) {
      this.state = 'OPEN';
      console.warn(`🔴 Circuit breaker OPENED for ${this.identifier} after ${this.failures} failures`);
    }
  }

  getState(): string {
    return this.state;
  }

  reset(): void {
    this.failures = 0;
    this.state = 'CLOSED';
    this.lastFailureTime = 0;
  }
}
